<template>
    <Modal
        :show="showing"
        :close="() => (showing = false)"
        :allow-click-out="true"
        :allow-escape="true"
        :vanilla="true"
    >
        <div class="search-container">
            <!-- header search input-->
            <div class="search-header">
                <label for="global-search-input">
                    <i v-if="!searching" class="uk-icon-search"></i>
                    <i v-else class="uk-icon-spinner"></i>
                </label>
                <input
                    id="global-search-input"
                    ref="input"
                    v-model="keyword"
                    autofocus
                    placeholder="Looking for something?"
                    autocomplete="off"
                />
            </div>
            <!-- search result list -->
            <div class="results">
                <!-- result group -->
                <template
                    v-if="
                        (results['Search Results'] &&
                            results['Search Results'].length) ||
                        (results['Last Visited'] &&
                            results['Last Visited'].length)
                    "
                >
                    <div
                        v-for="(groupResults, groupName) in results"
                        :key="groupName"
                    >
                        <template v-if="groupResults.length">
                            <!-- title of group -->
                            <div class="group-title">{{ groupName }}</div>
                            <!-- group items -->
                            <div
                                v-for="item in groupResults"
                                :key="item.subtitle"
                                class="group-items"
                            >
                                <div
                                    :class="[
                                        'item',
                                        {
                                            selected:
                                                selectedItem &&
                                                item.subtitle ===
                                                    selectedItem.subtitle,
                                        },
                                    ]"
                                    @click="onClickItem($event, item)"
                                >
                                    <!-- left icon -->
                                    <div class="icon">
                                        <img
                                            :src="`/img/icons/v3/linkicons/${item.icon}.svg`"
                                        />
                                    </div>
                                    <!-- right content -->
                                    <div class="content">
                                        <div class="title">
                                            {{ item.title }}
                                        </div>
                                        <div class="subtitle">
                                            {{ item.subtitle }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </div>
                </template>
                <div v-else class="empty-result">
                    <div v-if="!keyword">Start typing to search</div>
                    <div v-else>No results found</div>
                </div>
            </div>
            <!-- footer explain part -->
            <div class="footer">
                ↑ ↓ to navigate, Enter to select, ⌘ + Enter to open a new window
            </div>
        </div>
    </Modal>
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from 'vue-facing-decorator'
import Modal from '@/components/Modal.vue'
import { addVisitHistory } from '@/api/globalSearch'

@Component({
    components: { Modal },
})
export default class GlobalSearch extends Vue {
    public showing = false
    public searching = false
    public keyword = ''
    public results: object = {}
    // for using arrow, which is currently selected
    public selected = -1
    public delayTimer // delay search on typing

    @Prop({ type: Function }) public search!: (search: string) => object

    public mounted() {
        this.bindKeyboardEvent()
    }

    @Watch('showing')
    public onShowingChanged() {
        // do a initial search when modal opens up
        if (this.showing && !this.keyword) {
            this.doSearch()
        }

        if (this.showing) {
            setTimeout(() => (this.$refs.input as HTMLElement).focus(), 10)
        }
    }

    @Watch('keyword')
    public onKeywordChanged() {
        clearTimeout(this.delayTimer)
        this.delayTimer = setTimeout(() => {
            this.doSearch()
        }, 300)
    }

    public bindKeyboardEvent() {
        document.addEventListener('keydown', (event: KeyboardEvent) => {
            if (event.metaKey && event.key === 'k') {
                event.preventDefault()
                this.showing = !this.showing
            } else if (this.showing) {
                // event.preventDefault()
                if (event.key === 'ArrowUp') {
                    this.selected = Math.max(0, this.selected - 1)
                } else if (event.key === 'ArrowDown') {
                    this.selected = Math.min(
                        this.resultsFlat.length - 1,
                        this.selected + 1
                    )
                } else if (event.key === 'Enter' && this.selectedItem) {
                    this.onClickItem(event, this.selectedItem)
                }
            }
        })
    }

    public get resultsFlat() {
        let results = []
        for (const groupName of Object.keys(this.results)) {
            results = results.concat(this.results[groupName])
        }
        return results
    }

    public get selectedItem() {
        if (this.selected < 0) {
            return null
        }
        return this.resultsFlat[this.selected]
    }

    public async doSearch() {
        this.searching = true
        this.results = await this.search(this.keyword)
        this.searching = false
    }

    public onClickItem(e, item) {
        const newTab = e.metaKey
        // put click to history
        addVisitHistory(JSON.stringify(item))
        if (newTab) {
            window.open(item.href, '_blank')
        } else {
            location.href = item.href
        }
    }
}
</script>

<style scoped lang="less">
.search-container {
    .search-header {
        height: 65px;
        border-bottom: 1px solid #edeeef;
        display: flex;
        align-items: center;

        label {
            display: inline-block;
            width: 55px;
            text-align: center;
        }

        #global-search-input {
            height: 30px;
            width: calc(100% - 70px);
            font-size: 20px;
            border: none;
            outline-width: 0;
        }
    }

    .results {
        max-height: 60vh;
        overflow-y: auto;
        padding: 5px 0;

        .group-title {
            padding: 5px 15px;
            color: #889bab;
            font-size: 10px;
            font-weight: 500;
            text-transform: uppercase;
        }

        .group-items {
            .item {
                display: flex;
                align-items: center;
                height: 55px;
                cursor: pointer;

                &:hover,
                &.selected {
                    background-color: #f5f6f7;
                }

                .icon {
                    width: 50px;
                    text-align: center;
                }

                .content {
                    .title {
                        color: #27364b;
                        font-size: 14px;
                        font-weight: 500;
                    }

                    .subtitle {
                        color: #96a2ac;
                        font-size: 12px;
                        overflow: hidden;
                        white-space: nowrap;
                        text-overflow: ellipsis;
                        max-width: 500px;
                    }
                }
            }
        }

        .empty-result {
            height: 100px;
            display: flex;
            align-items: center;
            color: #96a2ac;

            div {
                text-align: center;
                width: 100%;
            }
        }
    }

    .footer {
        height: 50px;
        display: flex;
        align-items: center;
        padding-left: 20px;
        color: #96a2ac;
        font-size: 12px;
        box-shadow: 0 -1px 4px 2px rgba(223, 223, 223, 0.5);
    }
}
</style>
